/**
 * AI 사업 연계 로직 — FR-AI. 도슨트 RAG·다국어·추천(클라/서버 공유, 순수·테스트 가능).
 * 경량 구현: 임베딩/pgvector 없이 키워드 검색(RAG-lite). 운영 시 벡터 검색으로 교체.
 */

// ── 다국어 감지(FR-AI-02) ────────────────────────────────────
export type Lang = "ko" | "en" | "ja" | "zh";
export const LANG_NAME: Record<Lang, string> = {
  ko: "한국어",
  en: "English",
  ja: "日本語",
  zh: "中文",
};

/** 지원하는 말들 — 이 목록 밖은 안 받는다(키·필터에 쓰이므로 화이트리스트여야 한다). */
export const LANGS = ["ko", "en", "ja", "zh"] as const;

export function isLang(v: unknown): v is Lang {
  return typeof v === "string" && (LANGS as readonly string[]).includes(v);
}

/** 브라우저 언어(`ko-KR`·`en-US`) → 우리가 아는 말. 모르면 한국어. */
export function langFromTag(tag: string | undefined | null): Lang {
  const head = (typeof tag === "string" ? tag : "").toLowerCase().split("-")[0] ?? "";
  return isLang(head) ? head : "ko";
}

/** 스크립트 기반 간이 언어 감지. 한글>가나>한자>라틴. */
export function detectLanguage(text: string): Lang {
  if (typeof text !== "string") return "ko";
  if (/[぀-ヿ]/.test(text)) return "ja"; // 히라가나·가타카나
  if (/[가-힣]/.test(text)) return "ko"; // 한글
  if (/[一-鿿]/.test(text)) return "zh"; // 한자(가나 없음)
  if (/[a-zA-Z]/.test(text)) return "en";
  return "ko";
}

// ── RAG-lite 지식 검색(FR-AI-01) ─────────────────────────────
export interface KbChunk {
  id: string; // 비식별 근거 식별자(예: glossary#chumseongdae)
  title: string;
  text: string;
}

function tokenize(s: string): string[] {
  const raw = s
    .toLowerCase()
    .split(/[^a-z0-9가-힣ぁ-ゔァ-ヴ一-鿿]+/i)
    .filter((t) => t.length >= 2);
  const tokens = new Set(raw);
  for (const token of raw) {
    if (!/^[가-힣]+$/.test(token)) continue;
    // 자연스러운 질문의 조사 때문에 "첨성대가"가 자료의 "첨성대"를 놓치지 않게 한다.
    const stem = token.replace(
      /(?:에게서|한테서|으로부터|에서|에게|한테|까지|부터|처럼|보다|하고|이랑|으로|은|는|이|가|을|를|와|과|의|에|도|만|랑|로)$/,
      "",
    );
    if (stem.length >= 2) tokens.add(stem);
  }
  return [...tokens];
}

/** 쿼리와 키워드 겹침으로 상위 k개 청크 검색. 점수 0이면 제외. */
export function retrieveChunks(
  kb: KbChunk[],
  query: string,
  k = 3,
): KbChunk[] {
  const qs = new Set(tokenize(query));
  if (qs.size === 0) return [];
  const scored = kb
    .map((c) => {
      const hay = `${c.title} ${c.text}`.toLowerCase();
      let score = 0;
      for (const q of qs) if (hay.includes(q)) score += 1;
      return { c, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, k).map((x) => x.c);
}

// (제거됨) 개인화 추천(FR-AI-04) — 굿즈 상세(FR-GOODS, docs/build/16)가 상품 노출을 대체하면서
// 웹 호출처가 없는 죽은 기능이 되어 정리(2026-07). 복원 필요 시 git 히스토리 참조.
